import { Badge } from '@/components/ui/badge';
import { Clock, Sun, Moon, Sunrise, Sunset } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WelcomeHeaderProps {
  name: string;
  lastSeenAt?: string;
  motivationalQuote: string;
}

export function WelcomeHeader({ name, lastSeenAt, motivationalQuote }: WelcomeHeaderProps) {
  const now = new Date();
  const hour = now.getHours();

  // Determine greeting and icon based on time of day
  const getGreeting = () => {
    if (hour < 6) return { text: 'Buonanotte', icon: Moon, color: 'text-blue-600' };
    if (hour < 12) return { text: 'Buongiorno', icon: Sunrise, color: 'text-yellow-600' };
    if (hour < 18) return { text: 'Buon pomeriggio', icon: Sun, color: 'text-orange-500' };
    if (hour < 22) return { text: 'Buonasera', icon: Sunset, color: 'text-purple-600' };
    return { text: 'Buonanotte', icon: Moon, color: 'text-blue-600' };
  };

  const { text: greetingText, icon: GreetingIcon, color: iconColor } = getGreeting();

  // Format current date and time
  const formatCurrentDateTime = () => {
    return now.toLocaleDateString('it-IT', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Check if last seen was more than 24h ago
  const showLastSeen = () => {
    if (!lastSeenAt) return false;
    const lastSeen = new Date(lastSeenAt);
    const hoursAgo = (now.getTime() - lastSeen.getTime()) / (1000 * 60 * 60);
    return hoursAgo > 24;
  };

  const formatLastSeen = () => {
    if (!lastSeenAt) return '';
    const lastSeen = new Date(lastSeenAt);
    return lastSeen.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
    });
  };

  return (
    <div className="flex flex-col space-y-4">
      {/* Greeting and Name */}
      <div className="flex items-center gap-3">
        <GreetingIcon className={cn('h-6 w-6', iconColor)} />
        <h1 className="text-2xl font-semibold text-foreground">
          {greetingText}, {name}!
        </h1>
      </div>

      {/* Date and Time */}
      <div className="flex items-center gap-2 text-foreground">
        <Clock className="h-4 w-4" />
        <span className="text-sm capitalize">{formatCurrentDateTime()}</span>
        {showLastSeen() && (
          <Badge variant="outline" className="ml-2 text-xs">
            Ultimo accesso: {formatLastSeen()}
          </Badge>
        )}
      </div>

      {/* Motivational Quote */}
      <blockquote className="border-l-4 border-blue-200 pl-4 italic text-foreground">
        &ldquo;{motivationalQuote}&rdquo;
      </blockquote>
    </div>
  );
}
